import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Star, Send, CheckCircle, Quote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TestimonyWall = () => {
  const [testimonies, setTestimonies] = useState([]);
  const [form, setForm] = useState({ name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  useEffect(() => {
    fetchTestimonies();
  }, []);

  const fetchTestimonies = async () => {
    try {
      const res = await axios.get(`${API}/testimonies`);
      setTestimonies(res.data);
    } catch (err) {
      console.error("Failed to fetch testimonies:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonies`, form);
      setSubmitted(true);
      setForm({ name: "", message: "" });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error("Failed to submit testimony:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="testimonies"
      data-testid="testimony-section"
      className="py-20 md:py-32 bg-gradient-to-br from-amber-50/50 via-white to-red-50/30 relative overflow-hidden"
      ref={ref}
    >
      <div className="bubble-decoration bg-amber-400 w-56 h-56 -top-28 -left-28 animate-pulse-soft" />
      <div className="bubble-decoration bg-red-300 w-40 h-40 bottom-10 right-10 animate-pulse-soft" style={{ animationDelay: "1.5s" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Star size={20} className="text-amber-500" />
            <span className="font-accent text-2xl text-amber-600">Praise Reports</span>
            <Star size={20} className="text-amber-500" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-stone-900">
            Testimony <span className="text-red-600">Wall</span>
          </h2>
          <p className="text-base text-stone-500 mt-4 max-w-xl mx-auto">
            Read what God has been doing in the lives of our members, and share your own testimony.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-12">
          {/* Testimonies grid */}
          <div>
            {loading ? (
              <div className="grid sm:grid-cols-2 gap-5">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-40 bg-white rounded-2xl animate-pulse border border-stone-100" />
                ))}
              </div>
            ) : testimonies.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-[2rem] border border-stone-100">
                <Star size={40} className="mx-auto mb-3 text-stone-300" />
                <p className="text-stone-400">No testimonies yet. Be the first to share yours!</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                {testimonies.map((t, i) => (
                  <motion.div
                    key={t.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="bubbly-card bg-white p-6 rounded-2xl border border-stone-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)] relative"
                    data-testid={`testimony-card-${t.id}`}
                  >
                    <Quote size={24} className="text-red-100 absolute top-4 right-4" />
                    <p className="text-sm text-stone-600 leading-relaxed mb-4 relative z-10">
                      "{t.message}"
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-xs">
                        {t.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-semibold text-stone-800">{t.name}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Submit form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="bg-white rounded-[2rem] p-7 border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sticky top-28">
              <h3 className="text-lg font-bold text-stone-900 mb-1">Share Your Testimony</h3>
              <p className="text-xs text-stone-400 mb-5">Your testimony will appear after admin approval.</p>

              {submitted ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center"
                  data-testid="testimony-success-message"
                >
                  <CheckCircle size={36} className="mx-auto text-green-500 mb-3" />
                  <h4 className="text-base font-bold text-green-800">Testimony Submitted</h4>
                  <p className="text-green-600 text-sm mt-1">Thank you! It will appear once approved.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="testimony-form">
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1 block">Your Name *</label>
                    <Input
                      data-testid="testimony-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Enter your name"
                      className="rounded-full border-stone-200 bg-stone-50 px-5 py-3 h-auto"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1 block">Your Testimony *</label>
                    <Textarea
                      data-testid="testimony-message-textarea"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Share what God has done for you..."
                      rows={4}
                      className="rounded-2xl border-stone-200 bg-stone-50 px-5 py-3 resize-none"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="testimony-submit-btn"
                    className="w-full bg-red-600 text-white hover:bg-red-700 rounded-full px-6 py-3.5 font-semibold shadow-md hover:shadow-lg transition-transform duration-200 hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {submitting ? "Submitting..." : (<><Send size={16} /> Share Testimony</>)}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
